/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-01
 */
import { Injectable, inject, signal } from '@angular/core';
import {
  ClaveContextoProducto,
  guardarContextoDeProducto,
  leerContextoDeProducto,
  quitarContextoDeProducto,
} from '../auth/contexto-producto.storage';
import { Observable, tap } from 'rxjs';
import { HttpService } from '../http/http.service';
import { API_CONFIG } from '../crud/api.config';

/**
 * La configuracion que vale para el usuario en sesion, como la resuelve masters: el documento de
 * las plantillas asignadas a su compañia y a sus roles, por parametro y para el producto pedido
 * (HT_ARQ_012). El front no conoce defectos,
 * prioridades ni reglas de union entre roles.
 */
export interface ConfiguracionEfectiva {
  productoCodigo?: string | null;
  parametros: Record<string, unknown>;
}

/** Una tarjeta del dashboard de inicio. */
export interface TarjetaDashboard {
  codigo: string;
  tipo: 'CARD' | 'GRAFICO' | 'LISTA' | 'ALERTA';
  titulo?: string;
  parametros?: Record<string, unknown>;
}

/** Una zona del dashboard de inicio, con sus tarjetas o un componente incrustado. */
export interface ZonaDashboard {
  codigo: string;
  titulo?: string;
  columnas?: number;
  componente?: { codigo: string; parametros?: Record<string, unknown> };
  tarjetas?: TarjetaDashboard[];
}

/** El documento del parametro `DASHBOARD_INICIO`: la entrada del motor del dashboard. */
export interface DocumentoDashboard {
  titulo: string;
  descripcion?: string;
  zonas: ZonaDashboard[];
}

/**
 * Donde queda el codigo del producto activo: lo anota la seleccion de producto junto a su id, con
 * el `applicationCode` que el menu entrega por cada producto.
 */
const CLAVE_PRODUCTO_ACTIVO: ClaveContextoProducto = 'selectedProductCode';

/** El codigo del producto que dejo guardado la sesion, o nulo. */
function leerProductoGuardado(): string | null {
  try {
    return leerContextoDeProducto(CLAVE_PRODUCTO_ACTIVO);
  } catch {
    return null;
  }
}

/** Tarjeta del grafico del Inicio de Contabilidad, que lleva los meses en sus parametros. */
const TARJETA_GRAFICO_MESES = 'GRAFICO_CAUSADO_MES';

/**
 * La configuracion de la compañia y de los roles del usuario en sesion, leida de
 * `/parametros-asignacion/mi-configuracion` (HT_ARQ_012): el perfil visual (`APARIENCIA`) y el dashboard
 * de inicio de cada producto (`DASHBOARD_INICIO`).
 *
 * <p>El perfil se lee sin producto. El dashboard se pide por el PRODUCTO ACTIVO —el que el usuario
 * eligio, como lo usa el menu— la primera vez que alguien lo pregunta. Mientras no llegue, o si no
 * hay dashboard asignado, el Inicio no existe.</p>
 *
 * <p>El codigo del producto activo es una señal: lo fijan la eleccion del producto y el sidebar
 * cada vez que carga el menu del producto activo, de modo que una sesion que ya traia el producto
 * elegido lo recupera sin volver a elegirlo.</p>
 */
@Injectable({ providedIn: 'root' })
export class ConfiguracionRolService {
  private readonly http = inject(HttpService);
  private readonly url = `${API_CONFIG.masters.baseUrl}${API_CONFIG.masters.endpoints.parametrosAsignacion}/mi-configuracion`;

  /** La ultima configuracion leida sin producto, o nula si aun no se leyo. */
  readonly configuracion = signal<ConfiguracionEfectiva | null>(null);

  /** La configuracion leida de cada producto. */
  private readonly porProducto = signal<Record<string, ConfiguracionEfectiva>>({});

  /** Productos cuya configuracion ya se pidio. */
  private readonly pedidos = new Set<string>();

  /** Codigo del producto activo; nace del que dejo guardado la sesion. */
  private readonly codigoProducto = signal<string | null>(leerProductoGuardado());

  /**
   * Lee la configuracion del usuario en sesion, sin producto, y la deja vigente. Olvida la de los
   * productos: la proxima pregunta por un dashboard la vuelve a pedir.
   *
   * @returns la configuracion leida.
   */
  cargar(): Observable<ConfiguracionEfectiva> {
    this.pedidos.clear();
    this.porProducto.set({});
    return this.http
      .getContent<ConfiguracionEfectiva>(this.url)
      .pipe(tap((c) => this.configuracion.set(this.completa(c))));
  }

  /**
   * Un parametro de la configuracion sin producto.
   *
   * @param codigo codigo del parametro.
   * @returns su documento, o `undefined` si aun no se leyo.
   */
  parametro<T>(codigo: string): T | undefined {
    return this.configuracion()?.parametros?.[codigo] as T | undefined;
  }

  /**
   * El perfil visual: `UI_3.0` o `UI_DEFAULT`.
   *
   * @returns el perfil, o nulo si aun no se leyo.
   */
  uiProfile(): string | null {
    return this.parametro<{ perfilVisual?: string }>('APARIENCIA')?.perfilVisual ?? null;
  }

  /**
   * El codigo del producto activo.
   *
   * @returns el codigo, o nulo si aun no se eligio producto.
   */
  productoActivo(): string | null {
    return this.codigoProducto();
  }

  /**
   * Fija el producto activo: el que el usuario eligio o el que el sidebar resolvio de su menu.
   *
   * @param codigo codigo del producto (`applicationCode` del menu); nulo para olvidarlo.
   */
  fijarProductoActivo(codigo: string | null | undefined): void {
    const valor = codigo || null;
    try {
      if (valor) guardarContextoDeProducto(CLAVE_PRODUCTO_ACTIVO, valor);
      else quitarContextoDeProducto(CLAVE_PRODUCTO_ACTIVO);
    } catch {
      // Sin almacenamiento el producto vive solo en la señal.
    }
    if (this.codigoProducto() !== valor) this.codigoProducto.set(valor);
  }

  /**
   * El dashboard de inicio del producto activo. La primera vez lo pide a masters.
   *
   * @returns el documento, o `undefined` si aun no llego o no hay producto activo.
   */
  dashboard(): DocumentoDashboard | undefined {
    const producto = this.productoActivo();
    if (!producto) return undefined;
    const leida = this.porProducto()[producto];
    if (!leida) {
      this.pedir(producto);
      return undefined;
    }
    return leida.parametros?.['DASHBOARD_INICIO'] as DocumentoDashboard | undefined;
  }

  /**
   * Si el producto activo muestra la pestaña de Inicio: solo cuando su dashboard asignado trae al
   * menos una zona. Sin plantilla asignada, mientras no llega o si fallo, el Inicio no existe.
   *
   * @returns si la muestra.
   */
  inicioHabilitado(): boolean {
    const documento = this.dashboard();
    return !!documento && (documento.zonas ?? []).length > 0;
  }

  /**
   * Si la configuracion del producto activo ya se resolvio (con o sin dashboard, o con fallo).
   *
   * @returns si ya hay respuesta para el producto activo.
   */
  dashboardResuelto(): boolean {
    const producto = this.productoActivo();
    if (!producto) return false;
    this.dashboard();
    return this.porProducto()[producto] !== undefined;
  }

  /**
   * Un ajuste del Inicio del producto activo, leido de su dashboard: `mesesGrafico` (los meses de
   * la tarjeta del grafico) y `mesAnteriorSiVacio` (falso si alguna tarjeta lo apaga).
   *
   * @param clave el ajuste.
   * @returns el valor, o `undefined` si nadie lo resolvio aun.
   */
  delInicio<T>(clave: string): T | undefined {
    const documento = this.dashboard();
    if (!documento) return undefined;
    const tarjetas = (documento.zonas ?? []).flatMap((z) => z.tarjetas ?? []);
    switch (clave) {
      case 'mesesGrafico':
        return tarjetas.find((t) => t.codigo === TARJETA_GRAFICO_MESES)?.parametros?.['meses'] as
          | T
          | undefined;
      case 'mesAnteriorSiVacio': {
        const ajustes = tarjetas
          .map((t) => t.parametros?.['mesAnteriorSiVacio'])
          .filter((v) => v !== undefined);
        return (ajustes.length === 0 ? undefined : !ajustes.includes(false)) as T | undefined;
      }
      default:
        return undefined;
    }
  }

  /**
   * Un parametro de la configuracion de un producto, que no tiene por que ser el activo. La primera
   * vez pide a masters la configuracion de ese producto.
   *
   * @param producto codigo del producto.
   * @param codigo codigo del parametro.
   * @returns su documento, o `undefined` si aun no llego o el producto no lo tiene.
   */
  delProducto<T>(producto: string, codigo: string): T | undefined {
    const leida = this.porProducto()[producto];
    if (!leida) {
      this.pedir(producto);
      return undefined;
    }
    return leida.parametros?.[codigo] as T | undefined;
  }

  /**
   * Si la configuracion de un producto ya se resolvio (con o sin el parametro, o con fallo).
   *
   * @param producto codigo del producto.
   * @returns si ya hay respuesta para ese producto.
   */
  productoResuelto(producto: string): boolean {
    if (this.porProducto()[producto] === undefined) {
      this.pedir(producto);
      return false;
    }
    return true;
  }

  private pedir(producto: string): void {
    if (this.pedidos.has(producto)) return;
    this.pedidos.add(producto);
    // Se pide fuera del ciclo en curso: quien pregunta puede estar dentro de un computed, donde no
    // se escribe una señal.
    queueMicrotask(() =>
      this.http
        .getContent<ConfiguracionEfectiva>(`${this.url}?producto=${encodeURIComponent(producto)}`)
        .subscribe({
          next: (c) =>
            this.porProducto.update((todas) => ({ ...todas, [producto]: this.completa(c) })),
          // Si falla, el producto queda sin configuracion: nada se pinta.
          error: () =>
            this.porProducto.update((todas) => ({ ...todas, [producto]: { parametros: {} } })),
        }),
    );
  }

  private completa(c: ConfiguracionEfectiva | null | undefined): ConfiguracionEfectiva {
    return c?.parametros ? c : { parametros: {} };
  }
}
